import { ListFilter, ChevronDown, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export interface FilterDropdownProps<T extends string> {
  label: string;
  options: { value: T; label: string }[];
  selected: T[];
  onChange: (values: T[]) => void;
}

/**
 * Multi-select filter dropdown backing a TanStack column filter. Shared by the
 * league dashboard and roster filter bars.
 */
export function FilterDropdown<T extends string>({
  label,
  options,
  selected,
  onChange,
}: FilterDropdownProps<T>) {
  const isActive = selected.length > 0;

  const toggle = (value: T, checked: boolean) => {
    onChange(
      checked ? [...selected, value] : selected.filter((v) => v !== value),
    );
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            variant="outline"
            size="sm"
            className={`gap-1.5 text-xs font-normal rounded-md transition-colors ${
              isActive
                ? "border-primary bg-primary/10 text-primary hover:bg-primary/15"
                : "bg-surface dark:bg-surface text-text-muted hover:text-text-primary"
            }`}
          />
        }
      >
        <ListFilter className="size-3.5" />
        {label}
        {isActive && (
          <span className="flex items-center justify-center size-4 rounded-full bg-primary text-primary-foreground text-[10px] font-bold leading-none">
            {selected.length}
          </span>
        )}
        <ChevronDown className="size-3.5 opacity-60" />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-44 rounded-lg ring-1 ring-primary"
      >
        <DropdownMenuGroup>
          <DropdownMenuLabel className="text-xs">{label}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {options.map((opt) => (
            <DropdownMenuCheckboxItem
              key={opt.value}
              checked={selected.includes(opt.value)}
              onCheckedChange={(checked) => toggle(opt.value, checked)}
              className="text-xs"
            >
              {opt.label}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuGroup>
        {isActive && (
          <>
            <DropdownMenuSeparator />
            <button
              type="button"
              onClick={() => onChange([])}
              className="flex w-full items-center gap-1.5 px-2 py-1.5 text-xs text-text-muted hover:text-text-primary transition-colors"
            >
              <X className="size-3" />
              Clear filter
            </button>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
